import { create } from 'zustand';
import type { UserNotification } from '../types/notification';

interface NotificationState {
  unreadCount: number;
  recent: UserNotification[]; // newest-first, capped at CAP
  setUnreadCount: (n: number) => void;
  pushLive: (n: UserNotification) => void;
  applyReplay: (items: UserNotification[]) => void;
  markRead: (id: string) => void;
  reset: () => void;
}

const CAP = 30;

export const useNotificationStore = create<NotificationState>((set) => ({
  unreadCount: 0,
  recent: [],
  setUnreadCount: (n) => set({ unreadCount: Math.max(0, n) }),
  pushLive: (n) =>
    set((s) => {
      // De-dupe by id so replay + live for the same row collapses.
      const filtered = s.recent.filter((x) => x.id !== n.id);
      const next = [n, ...filtered].slice(0, CAP);
      return {
        recent: next,
        unreadCount: s.unreadCount + (n.read_at ? 0 : 1),
      };
    }),
  applyReplay: (items) =>
    set((s) => {
      const byId = new Map<string, UserNotification>(s.recent.map((x) => [x.id, x]));
      for (const n of items) byId.set(n.id, n);
      const all = Array.from(byId.values()).sort((a, b) =>
        a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0,
      );
      return { recent: all.slice(0, CAP) };
    }),
  markRead: (id) =>
    set((s) => {
      const recent = s.recent.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
      const stillUnread = recent.filter((n) => !n.read_at).length;
      return { recent, unreadCount: stillUnread };
    }),
  reset: () => set({ unreadCount: 0, recent: [] }),
}));
